import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SummaryCard from '../admin-dashboard/components/SummaryCard';
import ComplianceStatusBadge from '../admin-dashboard/components/ComplianceStatusBadge';
import { siteService } from '../../services/siteService';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/companyService';
import PlanLimitBanner from '../admin-dashboard/components/PlanLimitBanner';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import { useLanguage } from '../../contexts/LanguageContext';

const SitesList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [archivingId, setArchivingId] = useState(null);
  const [planLimitReached, setPlanLimitReached] = useState(false);
  const [siteUsage, setSiteUsage] = useState(null);
  const [company, setCompany] = useState(null);

  const role = userProfile?.role || 'admin';
  const userId = userProfile?.id;

  useEffect(() => {
    loadSites();
  }, [userProfile]);

  const loadSites = async () => {
    try {
      setLoading(true);
      let result;

      // This is the sites management page — the one place archived sites
      // SHOULD remain visible, because the page hosts the archive /
      // unarchive toggle. Every other role view filters them out
      // (NSherjFh cascade visibility).
      if (role === 'site_manager') {
        // site_manager is scoped to assigned sites only (BwT3Rzhb).
        // No company-wide fallback: zero assignments = zero sites + empty state.
        result = await siteService?.getSitesByUser({ includeArchived: true });
      } else {
        result = await siteService?.getAllSites({ includeArchived: true });
      }

      const allSites = result?.data || [];

      setSites(allSites);

      // Load plan usage for admin role
      if (role === 'admin') {
        const companyResult = await companyService?.getUserCompany();
        if (companyResult?.data?.id) {
          setCompany(companyResult?.data);
          const { data: usage } = await companyService?.getActiveSiteUsage(companyResult?.data?.id);
          if (usage) {
            setSiteUsage(usage);
            setPlanLimitReached(!usage?.can_add_more);
          }
        }
      }
    } catch (err) {
      console.error('[SitesList] load sites failed:', err);
      setError('Failed to load sites. Please refresh the page.');
      toast?.error('Failed to load sites. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const filteredSites = useMemo(() => {
    let result = [...sites];

    if (searchQuery) {
      result = result?.filter(site =>
        site?.site_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        site?.client_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      result = result?.filter(site => site?.status === statusFilter);
    }

    if (complianceFilter !== 'all') {
      result = result?.filter(site => site?.compliance_status === complianceFilter);
    }

    return result;
  }, [sites, searchQuery, statusFilter, complianceFilter]);

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || complianceFilter !== 'all';

  const summaryData = useMemo(() => {
    const totalSites = sites?.length || 0;
    const activeSites = sites?.filter(s => s?.status === 'active')?.length || 0;
    const avgCompliance = sites?.length > 0 
      ? Math.round(sites?.filter(s => s?.compliance_status === 'compliant')?.length / sites?.length * 100)
      : 0;
    const sitesNeedingAttention = sites?.filter(s => s?.compliance_status === 'critical' || s?.compliance_status === 'warning')?.length || 0;

    return [
      {
        title: t('totalSites'),
        value: totalSites,
        icon: 'Building2',
        color: 'var(--color-accent)',
        bgColor: 'bg-accent/10'
      },
      {
        title: t('activeSites'),
        value: activeSites,
        icon: 'CheckCircle2',
        color: 'var(--color-success)',
        bgColor: 'bg-success/10'
      },
      {
        title: t('avgCompliance'),
        value: `${avgCompliance}%`,
        icon: 'ShieldCheck',
        color: 'var(--color-success)',
        bgColor: 'bg-success/10'
      },
      {
        title: t('needsAttention'),
        value: sitesNeedingAttention,
        icon: 'AlertTriangle',
        color: 'var(--color-destructive)',
        bgColor: 'bg-destructive/10'
      }
    ];
  }, [sites, t]);

  const handleSiteClick = (siteId) => {
    navigate(`/sites/${siteId}`);
  };

  const handleArchiveSite = async (e, siteId, siteName) => {
    e?.stopPropagation();
    setArchivingId(siteId);
    try {
      const { error } = await siteService?.archiveSite(siteId);
      if (error) {
        toast?.error(`Failed to archive "${siteName}".`, {});
        console.error('[SitesList] Archive failed', { siteId, error });
      } else {
        toast?.success(`"${siteName}" has been archived.`);
        setPlanLimitReached(false);
        await loadSites();
      }
    } catch (err) {
      toast?.error('Archive failed. Please try again.', {});
    } finally {
      setArchivingId(null);
    }
  };

  const handleUnarchiveSite = async (e, siteId, siteName) => {
    e?.stopPropagation();
    setArchivingId(siteId);
    try {
      const { error } = await siteService?.unarchiveSite(siteId);
      if (error) {
        if (error?.message === 'SITE_LIMIT_REACHED') {
          navigate('/subscription-management');
        } else {
          toast?.error(`Failed to unarchive "${siteName}".`, {});
        }
        console.error('[SitesList] Unarchive failed', { siteId, error });
      } else {
        toast?.success(`"${siteName}" is now active.`);
        await loadSites();
      }
    } catch (err) {
      toast?.error('Unarchive failed. Please try again.', {});
    } finally {
      setArchivingId(null);
    }
  };

  const handleNewSiteClick = () => {
    if (planLimitReached && role === 'admin') {
      navigate('/subscription-management');
    } else {
      navigate('/site-setup-wizard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            {Array.from({ length: 4 })?.map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
                <div className="h-3 bg-muted rounded w-2/3 mb-3" />
                <div className="h-7 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
          <Skeleton variant="card" count={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <p className="text-destructive">{error}</p>
            <Button onClick={loadSites} className="mt-4">Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  const activeSiteCount = siteUsage?.active_count ?? siteUsage?.active_sites ?? 0;
  const maxSiteCount = siteUsage?.max_sites ?? 0;
  const extraSiteQty = company?.extra_site_quantity ?? 0;

  return (
    <>
      <Helmet>
        <title>Sites - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <SubscriptionReadOnlyBanner className="mb-6" />
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {role === 'site_manager' ? t('mySites') : role === 'client' ? t('sitesProgress') : t('sitesTitle')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {role === 'site_manager' ? t('sitesSubtitleManager')
                  : role === 'client' ? t('sitesSubtitleClient') : t('sitesSubtitleAdmin')}
              </p>
              {/* Site usage badge — visible to admins */}
              {siteUsage && maxSiteCount > 0 && role === 'admin' && (
                <span className={`inline-flex items-center gap-1.5 mt-2 text-xs font-medium px-2.5 py-1 rounded-full border ${
                  !siteUsage?.can_add_more
                    ? 'bg-destructive/10 border-destructive/20 text-destructive'
                    : activeSiteCount >= maxSiteCount * 0.8
                    ? 'bg-warning/10 border-warning/20 text-warning' :'bg-muted border-border text-muted-foreground'
                }`}>
                  <Icon name="Building2" size={12} />
                  Active sites: {activeSiteCount} / {maxSiteCount} included
                  {extraSiteQty > 0 && (
                    <span className="ml-1 text-accent">(+{extraSiteQty} add-on)</span>
                  )}
                </span>
              )}
            </div>
            {role === 'admin' && (
              <Button
                iconName="Plus"
                onClick={handleNewSiteClick}
                disabled={subscriptionReadOnly}
              >
                {t('addNewSite')}
              </Button>
            )}
          </div>

          {role === 'admin' && siteUsage && (
            <PlanLimitBanner
              usage={siteUsage}
              onUpgrade={() => navigate('/subscription-management')}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            {summaryData?.map((item) => (
              <SummaryCard
                key={item?.title}
                title={item?.title}
                value={item?.value}
                icon={item?.icon}
                iconColor={item?.bgColor}
                subtitle=""
                trend="neutral"
                trendValue={0}
                onClick={() => {}}
              />
            ))}
          </div>

          <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('searchSitesPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="all">{t('allStatuses')}</option>
                <option value="planning">Planning</option>
                <option value="active">{t('active')}</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
              <select
                value={complianceFilter}
                onChange={(e) => setComplianceFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="all">{t('allCompliance')}</option>
                <option value="compliant">{t('compliant')}</option>
                <option value="warning">{t('warning')}</option>
                <option value="critical">{t('critical')}</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              {filteredSites?.length} {filteredSites?.length === 1 ? t('siteFound') : t('sitesFound')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredSites?.map((site) => {
              const isArchived = site?.status === 'archived';
              const isArchivingThis = archivingId === site?.id;

              return (
                <div
                  key={site?.id}
                  onClick={() => handleSiteClick(site?.id)}
                  className="bg-card rounded-lg shadow-elevation-2 border border-border p-6 hover:shadow-elevation-3 transition-smooth cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0">
                        <Icon name="Building2" size={24} color="var(--color-accent)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground mb-1">{site?.site_name}</h3>
                        <p className="text-sm text-muted-foreground">{site?.client_name}</p>
                      </div>
                    </div>
                    <ComplianceStatusBadge status={site?.compliance_status} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Status</p>
                      <p className="text-sm font-medium text-foreground capitalize">{site?.status?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                      <p className="text-sm text-foreground">{new Date(site?.start_date)?.toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">End Date</p>
                      <p className="text-sm text-foreground">{new Date(site?.end_date)?.toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-sm text-muted-foreground">{site?.worker_count || 0} workers</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {role === 'admin' && (
                        <button
                          onClick={(e) => isArchived
                            ? handleUnarchiveSite(e, site?.id, site?.site_name)
                            : handleArchiveSite(e, site?.id, site?.site_name)
                          }
                          disabled={isArchivingThis}
                          className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-border hover:bg-muted transition-smooth disabled:opacity-50"
                          title={isArchived ? t('unarchive') : t('archive')}
                        >
                          {isArchivingThis ? (
                            <Icon name="Loader2" size={12} className="animate-spin" />
                          ) : (
                            <Icon name={isArchived ? 'ArchiveRestore' : 'Archive'} size={12} />
                          )}
                          <span>{isArchived ? t('unarchive') : t('archive')}</span>
                        </button>
                      )}
                      <Icon name="ArrowRight" size={20} color="var(--color-accent)" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredSites?.length === 0 ? (
            <div className="bg-card rounded-lg shadow-elevation-2 border border-border">
              <EmptyState
                icon={hasActiveFilters ? 'Search' : 'Building2'}
                title={hasActiveFilters ? t('noSitesMatch') : role === 'site_manager' ? t('noSitesAssignedManager') : t('noSitesYet')}
                description={hasActiveFilters ? t('noSitesMatchDesc') : role === 'site_manager' ? t('noSitesAssignedManagerDesc') : t('noSitesYetDesc')}
                ctaLabel={!hasActiveFilters && role === 'admin' ? t('addNewSite') : hasActiveFilters ? t('clearFilters') : ''}
                ctaAction={!hasActiveFilters && role === 'admin' ? handleNewSiteClick : hasActiveFilters ? () => { setSearchQuery(''); setStatusFilter('all'); setComplianceFilter('all'); } : null}
                ctaIcon={!hasActiveFilters ? 'Plus' : 'X'}
              />
            </div>
          ) : null}
        </main>
      </div>

    </>
  );
};

export default SitesList;